import { XHR_READY, XHR_SUCCESS_CODE } from '@opengov/stories-shared/constants';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { Upload as UploadIcon, Alert as AlertIcon } from '@opengov/react-capital-assets';
import { merge } from 'lodash';
import { styles as muiStyles, dropzoneBaseSx } from './ImageUploader.styles';
import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import Dropzone, { Accept, FileRejection, DropEvent } from 'react-dropzone';

import { imageUploader as msgs } from '../../../../locale/stories.en.locale.json';
import { getFileSizeInBytes, getS3SignUrl, IGetS3SignUrlParams } from '../../../common/utils';
import { MAX_IMAGE_UPLOAD_SIZE, MIME_IMAGE } from '../../../constants';
import { useToast } from '../../../components/Toast/context/useToast.context';

import { ConfirmationModal, IConfirmationModalProps } from '../ConfirmationModal';

export const DEFAULT_MAX_UPLOAD_SIZE = getFileSizeInBytes(MAX_IMAGE_UPLOAD_SIZE);

export enum RejectReason {
  UNKNOWN = 'unknown',
  NOT_EXIST = 'not_exist',
  WRONG_TYPE = 'wrong_type',
  MAX_SIZE_EXCEEDED = 'max_size_exceeded'
}

export type IImageUploaderProps = {
  entitySubdomain: string;
  accept?: Accept;
  activeStyle?: CSSProperties;
  children?: ReactNode;
  disabled?: boolean;
  imagePreviewClassName?: string;
  maxSize?: number;
  multiple?: boolean;
  requireConfirmationBeforeRemove?: boolean;
  confirmationModalProps?: Partial<IConfirmationModalProps>;
  rejectStyle?: CSSProperties;
  url?: string;
  style?: CSSProperties;
  onDrop?(acceptedFiles: File[], rejectedFiles?: File[]): void;
  onDropRejected?(rejectReason: RejectReason, rejectedFiles: File[], event: DropEvent): void;
  onProgressChange?(percent: number): void;
  onRemove?(): void;
  onUploadSuccess(url: string): void;
  onUploadFailure?(): void;
};

// Re-exported for existing consumers (e.g. the TinyMCE editor image upload path).
// The implementation lives in common/utils so it can be shared and unit-tested
// independently of this component's heavy render dependencies.
export { getS3SignUrl };
export type { IGetS3SignUrlParams };

export const getFileName = (url: string) => {
  const splitted = url.split('/');
  const bucketAndFilename = splitted[splitted.length - 1];
  if (!bucketAndFilename.includes('-')) return bucketAndFilename;
  const arr = bucketAndFilename.split('-');
  arr.shift();
  return arr.join('-');
};

const defaultDropzoneStyle: CSSProperties = { width: '100%' };
const CONFIRMATION_TEXT = 'Remove';
const DEFAULT_ACCEPT: Accept = { [MIME_IMAGE]: [] };


export const ImageUploader = (props: IImageUploaderProps) => {
  const {
    entitySubdomain,
    accept = DEFAULT_ACCEPT,
    activeStyle,
    children,
    disabled,
    imagePreviewClassName,
    maxSize = DEFAULT_MAX_UPLOAD_SIZE,
    multiple = false,
    requireConfirmationBeforeRemove,
    confirmationModalProps,
    rejectStyle,
    url,
    style = {},
    onDrop,
    onDropRejected,
    onProgressChange,
    onRemove,
    onUploadSuccess,
    onUploadFailure
  } = props;
  const { toggleToast } = useToast();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState<boolean>(false);
  const [currentUrl, setCurrentUrl] = useState<string | null>(url);

  useEffect(() => {
    setCurrentUrl(url);
  }, [url]);

  const updateProgress = (event) => {
    if (event.lengthComputable && event.loaded && event.total) {
      if (!isUploading) setIsUploading(true);
      const percent = Math.ceil((event.loaded / event.total) * 100);
      setProgressPercent(percent);
      if (onProgressChange) onProgressChange(percent);
    }
  };

  const handleError = (errorText: string) => {
    if (!currentUrl) {
      setError(errorText);
    } else {
      toggleToast({
        title: msgs.notSupported,
        body: errorText,
        severity: 'error'
      });
    }
  };

  const uploadFile = (file, signedRequest, resultUrl) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = updateProgress;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XHR_READY) {
        setIsUploading(false);
        if (xhr.status === XHR_SUCCESS_CODE) {
          setCurrentUrl(resultUrl);
          onUploadSuccess(resultUrl);
        } else {
          handleError(msgs.errorUploading);
          if (onUploadFailure) onUploadFailure();
        }
      }
    };
    xhr.open('PUT', signedRequest);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    setProgressPercent(1);
    if (onDrop) onDrop(acceptedFiles);
    const acceptedFile = acceptedFiles[0];
    if (!acceptedFile) return;
    setError(null);
    setIsUploading(true);
    const s3url = getS3SignUrl({
      entitySubdomain,
      fileName: acceptedFile.name,
      fileType: acceptedFile.type
    });
    // fetch (not XHR): in PSP mode auth (bearer + credentials) is attached by
    // the window.fetch wrapper in psp/pspEntry.tsx — raw XHR bypasses it and
    // the sign request 401s. The S3 PUT below stays on XHR for upload
    // progress events; it must not carry our auth headers anyway.
    fetch(s3url)
      .then((res) => {
        if (!res.ok) throw new Error(`sign-s3 failed: ${res.status}`);
        return res.json();
      })
      .then((response) => {
        uploadFile(acceptedFile, response.signedRequest, response.url);
      })
      .catch(() => {
        setIsUploading(false);
        handleError(msgs.failedGetUrl);
        if (onUploadFailure) onUploadFailure();
      });
  };

  const handleDropRejected = (fileRejections: FileRejection[], event: DropEvent) => {
    let rejectReason = RejectReason.UNKNOWN;
    const rejection = fileRejections[0];
    const file = rejection?.file;
    const acceptMimeTypes = Object.keys(accept);
    const acceptMimeType = acceptMimeTypes[0];
    if (!file) {
      rejectReason = RejectReason.NOT_EXIST;
      handleError(msgs.generalError);
    } else if (acceptMimeType && !file.type.startsWith(acceptMimeType.split('/')[0])) {
      rejectReason = RejectReason.WRONG_TYPE;
      handleError(msgs.incompatibleFiletypeError);
    } else if (file.size > maxSize) {
      rejectReason = RejectReason.MAX_SIZE_EXCEEDED;
      handleError(msgs.maxSizeExceededError);
    }
    const rejectedFiles = fileRejections.map((r) => r.file);
    if (onDropRejected) onDropRejected(rejectReason, rejectedFiles, event);
  };

  const handleRemove = () => {
    requireConfirmationBeforeRemove ? setShowRemoveConfirmation(true) : onRemove();
  };

  const renderRemoveConfirmation = () => {
    const handleClose = () => setShowRemoveConfirmation(false);
    const handleConfirm = () => {
      onRemove();
      handleClose();
    };
    return (
      <ConfirmationModal
        title={msgs.removeImage}
        dataTest="remove-confirmation-modal"
        confirmationText={CONFIRMATION_TEXT}
        confirmationRequired={true}
        {...confirmationModalProps}
        onCancel={handleClose}
        onProceed={handleConfirm}
      />
    );
  };

  const computedStyle = useMemo(() => merge(style, defaultDropzoneStyle), [style]);

  if (currentUrl && !isUploading) {
    return (
      <Box sx={muiStyles.resultContainer}>
        <Box sx={muiStyles.imagePreview} className={imagePreviewClassName}>
          <img src={currentUrl} data-test="image-preview" alt="Logo" />
        </Box>
        <Box sx={muiStyles.imageInfo}>
          <Typography variant="body2">{getFileName(currentUrl)}</Typography>
          <Box sx={muiStyles.controls}>
            <Dropzone accept={accept} disabled={disabled} maxSize={maxSize} multiple={multiple} onDrop={handleDrop} onDropRejected={handleDropRejected}>
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} style={{ display: 'flex' }}>
                  <input {...getInputProps()} aria-label={msgs.changeImage} />
                  <Button variant="outlined" color="secondary" size="small" disabled={disabled}>
                    {msgs.changeImage}
                  </Button>
                </div>
              )}
            </Dropzone>
            <Button
              variant="outlined"
              color="error"
              size="small"
              data-test="image-remove"
              onClick={handleRemove}
            >
              {msgs.remove}
            </Button>
          </Box>
        </Box>
        {showRemoveConfirmation && renderRemoveConfirmation()}
      </Box>
    );
  }

  return (
    <Dropzone
      accept={accept}
      disabled={disabled}
      maxSize={maxSize}
      multiple={multiple}
      onDrop={handleDrop}
      onDropRejected={handleDropRejected}
    >
      {({ getRootProps, getInputProps, isDragActive, isDragReject }) => {
        const dropzoneStyle = {
          ...computedStyle,
          ...(isDragReject && rejectStyle ? rejectStyle : {}),
          ...(isDragActive && !isDragReject && activeStyle ? activeStyle : {})
        };

        const renderContent = () => {
          if (error) {
            return (
              <Box sx={dropzoneBaseSx}>
                <SvgIcon component={AlertIcon} inheritViewBox sx={muiStyles.errorIcon} aria-hidden={true} />
                <Typography variant="body2" sx={muiStyles.errorText}>
                  {error}
                </Typography>
                <Button variant="outlined" color="secondary" size="small">{msgs.uploadImage}</Button>
              </Box>
            );
          }
          if (isUploading && progressPercent < 100) {
            return (
              <Box sx={dropzoneBaseSx} data-test="image-uploading">
                <CircularProgress size={24} aria-label="Uploading" />
                <Typography variant="body2" color="text.secondary">
                  {msgs.uploading}... {progressPercent}%
                </Typography>
              </Box>
            );
          }
          if (children) return children;
          return (
            <Box sx={dropzoneBaseSx} data-test="image-dropzone">
              <SvgIcon component={UploadIcon} inheritViewBox sx={muiStyles.uploadIcon} aria-hidden={true} />
              <Typography variant="body2" color="text.secondary">{msgs.dropboxText}</Typography>
              <Button variant="outlined" color="secondary" size="small" data-test="logo-upload" disabled={disabled}>
                {msgs.uploadImage}
              </Button>
            </Box>
          );
        };

        return (
          <div {...getRootProps()} style={dropzoneStyle}>
            <input {...getInputProps()} aria-label={msgs.uploadImage} />
            {renderContent()}
          </div>
        );
      }}
    </Dropzone>
  );
};
